// Wallet state management slice

import { create } from 'zustand';
import { Wallet, WalletAccount, WalletState } from '../types/wallet';
import { SUPPORTED_WALLETS } from '../utils/constants';

export interface WalletSlice extends WalletState {
  // Actions
  setWallet: (wallet: Wallet | null) => void;
  setAccount: (account: WalletAccount | null) => void;
  setConnecting: (isConnecting: boolean) => void;
  setError: (error: string | null) => void;
  setBalance: (balance: string) => void;
  clearWallet: () => void;
  connectWallet: (walletName: string) => Promise<boolean>;
  disconnectWallet: () => Promise<void>;
  getAvailableWallets: () => Wallet[];
  updateBalance: () => Promise<void>;
}

// Create wallet slice
export const walletSlice = (set: any, get: any): WalletSlice => ({
  // Initial state
  wallet: null,
  account: null,
  isConnecting: false,
  error: null,
  balance: '0',

  // Set wallet
  setWallet: (wallet: Wallet | null) => {
    set({ wallet });
    if (wallet && wallet.accounts.length > 0) {
      set({ account: wallet.accounts[0] });
    } else {
      set({ account: null });
    }
  },

  // Set account
  setAccount: (account: WalletAccount | null) => {
    set({ account });
  },

  // Set connecting state
  setConnecting: (isConnecting: boolean) => {
    set({ isConnecting });
  },

  // Set error
  setError: (error: string | null) => {
    set({ error });
  },

  // Set balance
  setBalance: (balance: string) => {
    set({ balance });
  },

  // Clear wallet
  clearWallet: () => {
    set({
      wallet: null,
      account: null,
      error: null,
      balance: '0',
    });
  },

  // Connect wallet
  connectWallet: async (walletName: string): Promise<boolean> => {
    const { setConnecting, setError, setWallet } = get();
    
    setConnecting(true);
    setError(null);

    try {
      // In a real implementation, this would connect to the actual wallet
      // For now, we'll simulate the connection
      const walletConfig = SUPPORTED_WALLETS.find(w => w.name === walletName);
      
      if (!walletConfig) {
        setError(`Wallet ${walletName} not found`);
        setConnecting(false);
        return false;
      }

      // Simulate connection delay
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Create mock wallet with account
      const mockWallet: Wallet = {
        name: walletConfig.name,
        icon: walletConfig.icon,
        version: walletConfig.version,
        accounts: [
          {
            address: `0x${Math.random().toString(16).substr(2, 64)}`,
            publicKey: `0x${Math.random().toString(16).substr(2, 64)}`,
            name: 'Account 1',
          },
        ],
      };

      setWallet(mockWallet);
      setConnecting(false);
      return true;
    } catch (error) {
      setError(`Failed to connect wallet: ${error instanceof Error ? error.message : 'Unknown error'}`);
      setConnecting(false);
      return false;
    }
  },

  // Disconnect wallet
  disconnectWallet: async (): Promise<void> => {
    const { clearWallet } = get();
    
    try {
      // In a real implementation, this would disconnect from the actual wallet
      clearWallet();
    } catch (error) {
      console.error('Error disconnecting wallet:', error);
    }
  },

  // Get available wallets
  getAvailableWallets: (): Wallet[] => {
    // In a real implementation, this would detect installed wallets
    // For now, we'll return the supported wallets
    return SUPPORTED_WALLETS.map(wallet => ({
      ...wallet,
      accounts: [],
    }));
  },

  // Update balance
  updateBalance: async (): Promise<void> => {
    const { setBalance, account } = get();
    
    if (!account) {
      setBalance('0');
      return;
    }

    try {
      // In a real implementation, this would fetch the balance from the blockchain
      // For now, we'll simulate with a random balance
      const mockBalance = (Math.random() * 10000).toFixed(2);
      setBalance(mockBalance);
    } catch (error) {
      console.error('Error updating balance:', error);
      setBalance('0');
    }
  },
});

// Export a hook to use only the wallet slice
export const useWalletStore = create<WalletSlice>((set, get) => ({
  ...walletSlice(set, get),
}));